import { useMemo } from 'react';
import type { ISubCategoryStatistics, ISiddurTopSectionStatistics, IUserScoreInfo } from '@shared/types';

export type BookType = 'siddur' | 'mishna' | 'tanach' | 'unknown';

export interface BookData {
  type: BookType;
  displayName: string;
  data: ISubCategoryStatistics | ISiddurTopSectionStatistics;
  isSiddur: boolean;
}

export interface AccordionItem {
  key: string;
  bookName: string;
  levelOneData: ISubCategoryStatistics | ISiddurTopSectionStatistics;
  isTeacher: boolean;
}

/**
 * Normalizes book names to ensure they are in the correct case
 * @param bookName - The book name to normalize
 * @returns Normalized book name in correct case
 */
const normalizeBookName = (bookName: string): string => {
  const bookNameLower = bookName.toLowerCase();
  
  // Map lowercase names to correct case
  const bookNameMap: Record<string, string> = {
    'mishna': 'Mishna',
    'torah': 'Torah',
    'neviim': 'Neviim',
    'ketuvim': 'Ketuvim',
    'siddur_ashkenaz': 'Siddur_Ashkenaz',
    'siddur': 'Siddur_Ashkenaz',
    'סידור אשכנז': 'Siddur_Ashkenaz',
    'סידור': 'Siddur_Ashkenaz'
  };
  
  return bookNameMap[bookNameLower] || bookName;
};

/**
 * Hook: Determines the type of book based on its name
 * @param bookName - The name of the book
 * @returns BookType - The type of the book (siddur, mishna, tanach, or unknown)
 */
export const useBookType = (bookName: string): BookType => {
  return useMemo(() => {
    // Siddur variations
    if (bookName === 'סידור אשכנז' || 
        bookName === 'סידור' || 
        bookName === 'siddur' || 
        bookName === 'Siddur_Ashkenaz') {
      return 'siddur';
    }
    
    // Mishna variations
    if (bookName === 'משנה' || bookName === 'Mishna') {
      return 'mishna';
    }
    
    // Tanach books
    if (['תורה', 'נביאים', 'כתובים', 'Torah', 'Neviim', 'Ketuvim'].includes(bookName)) {
      return 'tanach';
    }
    
    return 'unknown';
  }, [bookName]);
};

/**
 * Hook: Provides accordion data based on book type and data structure
 * This hook handles the complex logic of determining which data structure to use
 * and returns the data in the exact format the components expect
 * @param booksWithScores - Array of books with their score data
 * @param selectedBookName - Currently selected book name
 * @param isTeacher - Whether the current user is a teacher
 * @returns Array of accordion items with all necessary data for rendering
 */
export const useBookAccordionData = (
  booksWithScores: IUserScoreInfo[],
  selectedBookName: string,
  isTeacher: boolean
): AccordionItem[] => {
  return useMemo(() => {
    if (!booksWithScores.length) return [];

    const selectedBookScore = booksWithScores.find(
      (book: IUserScoreInfo) => book.book_name_en === selectedBookName
    )?.book_statistics;

    if (!selectedBookScore) return [];

    // Check if it's a Siddur book
    const isSiddur = selectedBookName === 'Siddur_Ashkenaz' ||
                     selectedBookName === 'Siddur_Ashkenaz' ||
                     selectedBookName === 'סידור אשכנז' ||
                     selectedBookName === 'סידור' ||
                     selectedBookName === 'siddur';

    // For Siddur - use top_section_array (each item is ISiddurTopSectionStatistics)
    if (isSiddur && selectedBookScore.top_section_array) {
      return selectedBookScore.top_section_array.map((itemData: any, index: number) => ({
        key: `levelOne-siddur-${index}`,
        bookName: normalizeBookName(selectedBookName),
        levelOneData: itemData as ISiddurTopSectionStatistics, // Each item is ISiddurTopSectionStatistics
        isTeacher
      }));
    }

    // For regular books - use sub_category_array
    if (selectedBookScore.sub_category_array) {
      return selectedBookScore.sub_category_array.map((itemData: ISubCategoryStatistics, index: number) => ({
        key: `levelOne-${index}`,
        bookName: normalizeBookName(selectedBookName),
        levelOneData: itemData, // This is ISubCategoryStatistics
        isTeacher
      }));
    }

    return [];
  }, [booksWithScores, selectedBookName, isTeacher]);
};

/**
 * Utility function: Returns the appropriate accordion component name based on book type
 * This is NOT a hook - it's a regular utility function
 * @param bookType - The type of the book
 * @returns String name of the component to render
 */
export const getAccordionComponentName = (bookType: BookType): string => {
  switch (bookType) {
    case 'siddur':
      return 'SiddurAccordion';
    case 'mishna':
      return 'MishnaAccordion';
    case 'tanach':
    default:
      return 'TanachAccordion';
  }
};
